import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { invoiceAPI } from 'redux/invoiceAPI';
import { ItemType } from '@payer-admin-ui/api/src/invoices/models/ItemType';
import { FetchingState } from '@payer-admin-ui/components-v2/src/types/state';
import { LocalDate } from '@payer-admin-ui/api/src/external/java/LocalDate';
import { CurrencyCode } from '@payer-admin-ui/api/src/invoices/models/CurrencyCode';
import { DeliveryType } from '@payer-admin-ui/api/src/invoices/models/DeliveryType';
import { CalculationMode, OrderCustomerV3 } from '@payer-admin-ui/api/src/invoices/responses/InvoiceResponseV3';
import { LanguageCode } from '@payer-admin-ui/api/src/invoices/models/LanguageCode';
import i18n from 'i18next';
import { toNumber, toNumberOrNull } from '@payer-admin-ui/components-v2/src/utils/numbers';
import { LineItemWithDecimalAmountExcludingVat } from '@payer-admin-ui/api/src/invoices/requests/CreateInvoiceRequestV3';
import { fetchInvoice, upsertInvoiceInListView } from '../invoicesGlobalState';
import { showSnackbar } from '../../../../redux/snackbar';
import { MerchantAdminDispatch } from '../../../../redux/store';

export const creditInvoiceManuallyExVat = createAsyncThunk<
  void,
  {
    invoiceNumber: number;
    invoiceDate?: LocalDate;
    orderReference?: string;
    invoiceCustomer?: OrderCustomerV3;
    currencyCode?: CurrencyCode;
    deliveryType?: DeliveryType;
    standalone: boolean;
    item: LineItemWithDecimalAmountExcludingVat;
    languageCode?: LanguageCode;
    calculationMode: CalculationMode;
  },
  { dispatch: MerchantAdminDispatch }
>(
  'invoices/actions/CreditInvoiceManuallyModal/credit',
  async (
    {
      invoiceNumber,
      invoiceDate,
      orderReference,
      invoiceCustomer,
      currencyCode,
      deliveryType,
      standalone,
      item,
      languageCode,
      calculationMode,
    },
    { dispatch },
  ) => {
    const createdCreditInvoice = await invoiceAPI.actions.creditInvoiceExVatManually({
      invoiceNumber,
      invoiceDate,
      orderReference,
      invoiceCustomer,
      currencyCode,
      deliveryType,
      standalone,
      languageCode,
      items: [
        {
          itemType: ItemType.FREEFORM,
          calculationMode: calculationMode,
          position: 0,
          articleNumber: item.articleNumber,
          description: item.description,
          quantity: toNumber(item.quantity),
          unit: item.unit,
          unitPriceExcludingVat: toNumber(item.unitPriceExcludingVat),
          vatPercentage: toNumber(item.vatPercentage),
          salesAccountCode: toNumberOrNull(item.salesAccountCode) || undefined,
          vatAccountCode: toNumberOrNull(item.vatAccountCode) || undefined,
          additionalDetail: item.additionalDetail,
          costCenter: item.costCenter,
        },
      ],
    });
    dispatch(upsertInvoiceInListView(createdCreditInvoice));
    dispatch(fetchInvoice({ invoiceNumber }));
    dispatch(
      showSnackbar(i18n.t('invoices.actions.creditInvoiceManually.success', { invoiceNumber }), {
        severity: 'success',
      }),
    );
  },
);

export type CreditInvoiceManuallyModalState = {
  [invoiceNumber: string]: FetchingState | undefined;
};
const initialState: CreditInvoiceManuallyModalState = {};
const creditInvoiceManuallyExVatModalSlice = createSlice({
  name: 'invoices/actions/CreditInvoiceManuallyModal',
  initialState,
  reducers: {
    reset: (state, { payload: { invoiceNumber } }) => {
      delete state[invoiceNumber];
    },
  },
  extraReducers: builder => {
    builder.addCase(creditInvoiceManuallyExVat.pending, (state, { meta: { arg } }) => {
      state[arg.invoiceNumber] = { isFetching: true, success: undefined, error: null };
    });
    builder.addCase(creditInvoiceManuallyExVat.fulfilled, (state, { meta: { arg } }) => {
      state[arg.invoiceNumber] = { isFetching: false, success: true, error: null };
    });
    builder.addCase(creditInvoiceManuallyExVat.rejected, (state, { meta: { arg }, error }) => {
      state[arg.invoiceNumber] = { isFetching: false, success: false, error };
    });
  },
});

export const {
  actions: { reset },
  reducer: creditInvoiceManuallyExVatModalReducer,
} = creditInvoiceManuallyExVatModalSlice;
